import {Box, Center, Hide, HStack, Icon, Show, Text, VStack} from 'platform/foundation';
import styled from 'styled-components';

import {useLocation, useParams} from 'react-router-dom';

import {isNil} from 'ramda';

import {useNavigate} from 'shared/web';

import {useFormatAlternativeTotal} from '../../hooks/useFormatAlternativeTotal';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {useViewedAlternative} from '../../hooks/useViewedAlternative';
import {sortByLetter} from '../../utils/buildComparisonRows';
import {getAlternativeChoiceStatus} from '../../utils/getAlternativeChoiceStatus';
import {getAlternativeLabel} from '../../utils/getAlternativeLabel';
import {translateWithFallback as t} from '../../utils/translateWithFallback';

/**
 * "Your alternatives" tabs under the navigation of an offer with alternatives. A tab switches the
 * vehicle the page shows; comparing all of them side by side is a separate action.
 */
export function AlternativesStrip() {
  const {offerAlternatives} = useGetOnlineOfferData();
  const {activeAlternative, viewAlternative} = useViewedAlternative();
  const params = useParams<{offerId: string}>();
  const location = useLocation();
  const navigate = useNavigate();
  const formatTotal = useFormatAlternativeTotal();

  if (isNil(offerAlternatives)) {
    return null;
  }

  const caption = t('alternatives.strip.caption', 'Your alternatives');

  // A quiet text action, not a filled button: it belongs to the tab bar and must not outweigh it.
  const compareButton = (
    <StyledCompareButton
      type="button"
      onClick={() => navigate({pathname: `/${params.offerId}/compare`, search: location.search})}
      data-testid="alternativesStrip-compare"
    >
      <HStack spacing={2} align="center">
        <Icon value="action/compare_arrows" size={4} color="text.link" />
        <Text size="small" alternative color="link" noWrap>
          {t('alternatives.strip.compare', 'Compare alternatives')}
        </Text>
      </HStack>
    </StyledCompareButton>
  );

  return (
    <Box
      borderBottom="1px solid"
      borderColor="border.emphasisDefault"
      data-testid="alternativesStrip"
    >
      <Center justify="center" width="100%">
        {/* Fixed width, not flex-grow: the tabs must scroll inside, not widen the page. */}
        <Box width="100%" maxWidth={432} paddingTop={2} paddingHorizontal={[3, 8, 8, 20]}>
          {/* The tabs sit last and flush on the divider, so the active underline joins it. */}
          <Show onMobile>
            <HStack justify="space-between" align="center" spacing={3}>
              <Text size="xSmall" color="secondary" noWrap>
                {caption}
              </Text>
              {compareButton}
            </HStack>
          </Show>
          <HStack align="center" spacing={4}>
            <Hide onMobile>
              <Text size="xSmall" color="secondary" noWrap>
                {caption}
              </Text>
            </Hide>
            <StyledTabs aria-label={caption}>
              {sortByLetter(offerAlternatives.alternatives).map((alternative) => {
                const isActive = alternative.id === activeAlternative?.id;
                const choiceStatus = getAlternativeChoiceStatus(offerAlternatives, alternative);

                return (
                  <StyledTab
                    key={alternative.id}
                    type="button"
                    $isActive={isActive}
                    aria-current={isActive ? 'page' : undefined}
                    onClick={() => viewAlternative(alternative)}
                    data-testid={`alternativesStrip-${alternative.letter}`}
                  >
                    <VStack align="flex-start">
                      <HStack spacing={1} align="center">
                        <Text
                          size="xSmall"
                          alternative
                          color={isActive ? 'primary' : 'secondary'}
                          noWrap
                        >
                          {getAlternativeLabel(alternative)}
                        </Text>
                        <Show when={choiceStatus !== 'NONE'}>
                          <Icon value="good" size={4} color="text.successDark" />
                          <StyledVisuallyHidden>
                            {choiceStatus === 'PREPARING'
                              ? t('alternatives.strip.preparing', 'Being prepared')
                              : t('alternatives.yourSelection', 'Your selection')}
                          </StyledVisuallyHidden>
                        </Show>
                      </HStack>
                      <Text size="xSmall" color="secondary" noWrap>
                        {formatTotal(alternative)}
                      </Text>
                    </VStack>
                  </StyledTab>
                );
              })}
            </StyledTabs>
            <Hide onMobile>{compareButton}</Hide>
          </HStack>
        </Box>
      </Center>
    </Box>
  );
}

const StyledTabs = styled.nav`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;
  gap: ${({theme}) => theme.getSize(1)};
  overflow-x: auto;
  scrollbar-width: none;
`;

const StyledTab = styled.button<{$isActive: boolean}>`
  flex-shrink: 0;
  padding: ${({theme}) => `${theme.getSize(2)} ${theme.getSize(3)} ${theme.getSize(3)}`};
  border: none;
  border-bottom: ${({theme, $isActive}) =>
    `calc(${theme.getSize(1)} / 2) solid ${$isActive ? theme.colors.accent.base : 'transparent'}`};
  background: none;
  cursor: pointer;
  text-align: left;
  transition-property: border-color;
  transition-duration: 150ms;
  transition-timing-function: ease-out;

  &:hover {
    border-bottom-color: ${({theme, $isActive}) =>
      $isActive ? theme.colors.accent.base : theme.colors.border.emphasisDefault};
  }

  &:focus-visible {
    outline: ${({theme}) => `calc(${theme.getSize(1)} / 2) solid ${theme.colors.accent.base}`};
    outline-offset: ${({theme}) => `calc(${theme.getSize(1)} / -2)`};
    border-radius: ${({theme}) => theme.radii.small};
  }
`;

const StyledCompareButton = styled.button`
  flex-shrink: 0;
  /* No side padding: the label lines up with the page's right edge. Height keeps the hit area. */
  height: ${({theme}) => theme.getSize(8)};
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
    text-underline-offset: ${({theme}) => theme.getSize(1)};
  }

  &:focus-visible {
    outline: ${({theme}) => `calc(${theme.getSize(1)} / 2) solid ${theme.colors.accent.base}`};
    outline-offset: ${({theme}) => `calc(${theme.getSize(1)} / 2)`};
    border-radius: ${({theme}) => theme.radii.small};
  }
`;

const StyledVisuallyHidden = styled.span`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
`;
